<template>
  <v-card
    class="filter-selector pa-3 user-select-none"
    :class="{
      shown
    }"
    v-on-click-outside="closeFilterSelect"
    data-html2canvas-ignore
  >
    <app-loader
      :action="loadUsers"
    >
      <div class="d-flex justify-end mb-2">
        <v-icon @click="shown = false">
          close
        </v-icon>
      </div>
      <v-table fixed-header>
        <!-- header -->
        <thead>
          <tr class="position-sticky top-0 bg-white">
            <td
              class="w-0 bg-white position-sticky left-0"
              style="z-index: 1"
            />
            <!-- users -->
            <td
              v-for="user in users"
              :key="user.id"
              class="px-3 text-center"
            >
              <v-badge
                location="bottom right"
                color="#cccccc"
                class="cursor-pointer"
                :class="{
                  'opacity-50': internalHiddenCategories[user.id]?.length === $auth.user!.account.categories.length,
                }"
                offset-x="-5"
                @click="toggleColumn(user.id)"
              >
                <template #badge>
                  <v-icon
                    size="16"
                    color="#747474"
                  >
                    {{ internalHiddenCategories[user.id]?.length === $auth.user!.account.categories.length ? 'visibility' : 'visibility_off' }}
                  </v-icon>
                </template>
                <profile-image
                  :user="user"
                  size="34"
                  class="mx-auto"
                />
              </v-badge>
            </td>
          </tr>
        </thead>
        <tr
          v-for="category in $auth.user!.account.categories"
          :key="category.id"
          class="bg-white"
        >
          <!-- category name -->
          <td class="bg-white position-sticky left-0">
            <div
              class="d-flex align-center justify-space-between flex-nowrap"
              :class="{
                'opacity-50': users.every(user => internalHiddenCategories[user.id]?.includes(category.id)),
              }"
            >
              <div style="text-wrap: nowrap;">
                {{ category.name }}
              </div>
              <v-btn
                flat
                size="small"
                @click="toggleRow(category.id)"
                icon
                class="pa-0"
                height="25"
                width="35"
                color="transparent"
                base-color="transparent"
              >
                <v-icon color="#747474">
                  {{ users.every(user => internalHiddenCategories[user.id]?.includes(category.id)) ? 'visibility' : 'visibility_off' }}
                </v-icon>
              </v-btn>
            </div>
          </td>
          <td
            v-for="user in users"
            :key="user.id"
            class="text-center"
          >
            <div
              class="category-dot mx-0 cursor-pointer"
              :style="{backgroundColor: internalHiddenCategories[user.id]?.includes(category.id) ? '#e6efec' : category?.hex_code}"
              style="vertical-align: middle;"
              @click="toggle(user.id, category.id)"
            />
          </td>
        </tr>
      </v-table>
    </app-loader>
  </v-card>
</template>

<script lang="ts">
import { vOnClickOutside } from '@vueuse/components'
import { mapState } from 'pinia'
import { useCalendarStore } from '@/stores/calendar'
import { useUsersStore } from '@/stores/users'
import AppLoader from "@/components/AppLoader.vue"
import ProfileImage from "@/components/ProfileImage.vue"

export default {
  components: {ProfileImage, AppLoader},
  directives: {
    onClickOutside: vOnClickOutside,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      users: [] as App.Models.User[],
    }
  },
  computed: {
    ...mapState(useCalendarStore, ['hiddenCategories']),
    shown: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    internalHiddenCategories: {
      get() {
        return this.hiddenCategories
      },
      set(value) {
        this.hiddenCategories = value
      },
    }
  },
  mounted() {
  },
  methods: {
    closeFilterSelect() {
      if (!this.shown) return
      setTimeout(() => {
        this.shown = false
      }, 10)
    },
    async loadUsers() {
      this.users = await useUsersStore().loadUsers()
    },
    toggle(userId: number, categoryId: number) {
      const index = this.internalHiddenCategories[userId]?.indexOf(categoryId)
      if (index === -1 || index === undefined) {
        if (!this.internalHiddenCategories[userId]) {
          this.internalHiddenCategories[userId] = []
        }
        this.internalHiddenCategories[userId].push(categoryId)
      } else {
        this.internalHiddenCategories[userId].splice(index, 1)
      }
    },
    toggleRow(categoryId: number) {
      if (this.users.every(user => this.internalHiddenCategories[user.id]?.includes(categoryId))) {
        this.users.forEach(user => {
          const index = this.internalHiddenCategories[user.id]?.indexOf(categoryId)
          if (index !== -1) {
            this.internalHiddenCategories[user.id].splice(index, 1)
          }
        })
      } else {
        this.users.forEach(user => {
          if (!this.internalHiddenCategories[user.id]) {
            this.internalHiddenCategories[user.id] = []
          }
          if (!this.internalHiddenCategories[user.id].includes(categoryId)) {
            this.internalHiddenCategories[user.id].push(categoryId)
          }
        })
      }
    },
    toggleColumn(userId: number) {
      if (this.internalHiddenCategories[userId]?.length === this.$auth.user!.account.categories.length) {
        this.internalHiddenCategories[userId] = []
      } else {
        this.internalHiddenCategories[userId] = this.$auth.user!.account.categories.map(category => category.id)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.filter-selector {
  min-width: 250px;
  width: 77%;
  max-width: 650px;

  position: fixed;
  left: 50%;
  z-index: 52;
  transform: translate(-50%, 0);
  bottom: -360px;
  transition: bottom 0.3s;
  &.shown {
    bottom: 20px;
  }
}

.v-table {
  max-height: 210px;
  overflow: auto;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;

  :deep() .v-table__wrapper {
    overflow: visible;
  }
}

:deep() {
  .v-badge__badge {
    min-width: 0;
    width: 17px;
    height: 17px;
  }
}
</style>
